import { useState, useEffect, useMemo, useCallback } from 'react';
import communityService, {
  MabarParticipant,
  CreateGamePayload,
  CreateGameLineupEntry,
  GameDetailResponse,
  GameListItem,
  CustomFormField,
} from '@/services/communityService';
import {
  LineupSlot,
  parseFormatFromPlayingStyle,
  generateOneTeam,
  getDefaultRoleFromPosition,
  getParticipantEffectivePosition,
  getParticipantEffectiveTeam,
  getParticipantEffectivePlayerNumber,
} from './createGameUtils';
import { type SavedTeamItem } from './CreateGameSavedTeamsList';

const DEFAULT_FORMAT_FUTSAL = 5;
const DEFAULT_FORMAT_MINI_SOCCER = 7;

export type SideMode = 'choice' | 'saved_list' | 'lineup';

function toParticipantId(v: number | string | undefined): number {
  if (v === undefined || v === null) return 0;
  const n = typeof v === 'string' ? parseInt(v, 10) : v;
  return Number.isNaN(n) ? 0 : n;
}

/** Unik per tim tersimpan (nama + lineup participant ids) untuk filter tim yang sudah dipilih sisi lain */
function getSavedTeamKey(item: SavedTeamItem): string {
  const ids = item.lineup.map((s) => s.participantId).sort((a, b) => a - b);
  return `${item.teamName}|${ids.join(',')}`;
}

function buildSavedTeamsFromGames(games: GameDetailResponse[]): SavedTeamItem[] {
  // Map normalized teamName -> { originalName: string, lineupMap: Map<number, LineupSlot> }
  const teamMap = new Map<string, { originalName: string; lineupMap: Map<number, LineupSlot> }>();

  games.forEach((game) => {
    const processSide = (teamName: string | undefined, lineups: GameDetailResponse['lineups']) => {
      const trimmed = (teamName || '').trim();
      if (!trimmed || !lineups || !lineups.length) return;
      const key = trimmed.toLowerCase();

      if (!teamMap.has(key)) {
        teamMap.set(key, { originalName: trimmed, lineupMap: new Map() });
      }
      const existing = teamMap.get(key)!;

      lineups.forEach((l) => {
        const pid = toParticipantId(l.player_participant_id);
        if (!pid) return;

        const currentSlot = existing.lineupMap.get(pid);
        const resolvedNumber =
          l.player_number !== null && l.player_number !== undefined
            ? l.player_number
            : currentSlot?.playerNumber ?? null;

        existing.lineupMap.set(pid, {
          participantId: pid,
          name: l.name,
          role: l.role ?? currentSlot?.role ?? '',
          isStarting: currentSlot ? currentSlot.isStarting : l.is_starting,
          playerNumber: resolvedNumber,
        });
      });
    };

    const homeLineups = (game.lineups || []).filter((l) => l.team_side === 'home');
    const awayLineups = (game.lineups || []).filter((l) => l.team_side === 'away');
    processSide(game.home_team_name, homeLineups);
    processSide(game.away_team_name, awayLineups);
  });

  const out: SavedTeamItem[] = [];
  teamMap.forEach((val) => {
    out.push({
      teamName: val.originalName,
      lineup: Array.from(val.lineupMap.values()),
    });
  });
  return out;
}

export interface UseCreateGameModalParams {
  isOpen: boolean;
  eventSlug: string;
  eventSportCategorySlug: string;
  eventCustomForm?: CustomFormField[];
  gameSlug?: string;
  isSparring?: boolean;
  teamAName?: string;
  teamBName?: string;
  onClose: () => void;
  onSuccess: (wasEdit?: boolean) => void;
}

export function useCreateGameModal({
  isOpen,
  eventSlug,
  eventSportCategorySlug,
  eventCustomForm,
  gameSlug,
  isSparring = false,
  teamAName = 'Tim A',
  teamBName = 'Tim B',
  onClose,
  onSuccess,
}: UseCreateGameModalParams) {
  const isEditMode = Boolean(gameSlug);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [loadingGame, setLoadingGame] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchHome, setSearchHome] = useState('');
  const [searchAway, setSearchAway] = useState('');
  const [homeTeamName, setHomeTeamName] = useState(isSparring ? teamAName : 'Tim A');
  const [awayTeamName, setAwayTeamName] = useState(isSparring ? teamBName : 'Tim B');
  const [homeLineup, setHomeLineup] = useState<LineupSlot[]>([]);
  const [awayLineup, setAwayLineup] = useState<LineupSlot[]>([]);
  const [homeSideMode, setHomeSideMode] = useState<SideMode>(isSparring ? 'lineup' : 'choice');
  const [awaySideMode, setAwaySideMode] = useState<SideMode>(isSparring ? 'lineup' : 'choice');
  const [error, setError] = useState<string | null>(null);
  const [generateErrorHome, setGenerateErrorHome] = useState<string | null>(null);
  const [generateErrorAway, setGenerateErrorAway] = useState<string | null>(null);
  const [selectedSavedTeamKeyForHome, setSelectedSavedTeamKeyForHome] = useState<string | null>(
    null
  );
  const [selectedSavedTeamKeyForAway, setSelectedSavedTeamKeyForAway] = useState<string | null>(
    null
  );
  const [allGames, setAllGames] = useState<GameDetailResponse[]>([]);
  const defaultFormat =
    eventSportCategorySlug === 'futsal' ? DEFAULT_FORMAT_FUTSAL : DEFAULT_FORMAT_MINI_SOCCER;
  const [formatNumber, setFormatNumber] = useState<number>(defaultFormat);

  const maxStarting = formatNumber;
  const minPlayers = formatNumber;
  const sportLabel = eventSportCategorySlug === 'futsal' ? 'Futsal' : 'Mini Soccer';

  const confirmedParticipants = useMemo(
    () => participants.filter((p) => p.status === 'confirmed'),
    [participants]
  );

  const selectedIds = useMemo(() => {
    const ids = new Set<number>();
    homeLineup.forEach((s) => {
      const id = toParticipantId(s.participantId);
      if (id) ids.add(id);
    });
    awayLineup.forEach((s) => {
      const id = toParticipantId(s.participantId);
      if (id) ids.add(id);
    });
    return ids;
  }, [homeLineup, awayLineup]);

  const availableParticipants = useMemo(
    () =>
      confirmedParticipants.filter((p) => {
        const id = toParticipantId(p.id);
        return id > 0 && !selectedIds.has(id);
      }),
    [confirmedParticipants, selectedIds]
  );

  const teamAParticipants = useMemo(() => {
    if (!isSparring) return availableParticipants;
    return availableParticipants.filter(
      (p) => !p.team || p.team.trim().toLowerCase() === teamAName.trim().toLowerCase()
    );
  }, [availableParticipants, isSparring, teamAName]);

  const teamBParticipants = useMemo(() => {
    if (!isSparring) return availableParticipants;
    return availableParticipants.filter(
      (p) => p.team && p.team.trim().toLowerCase() === teamBName.trim().toLowerCase()
    );
  }, [availableParticipants, isSparring, teamBName]);

  const filterBySearch = useCallback((list: MabarParticipant[], q: string) => {
    if (!q.trim()) return list;
    const lower = q.trim().toLowerCase();
    return list.filter((p) => {
      const effPos = getParticipantEffectivePosition(p) || '';
      const effTeam = getParticipantEffectiveTeam(p) || '';
      return (
        p.name.toLowerCase().includes(lower) ||
        (p.role && p.role.toLowerCase().includes(lower)) ||
        effPos.toLowerCase().includes(lower) ||
        effTeam.toLowerCase().includes(lower)
      );
    });
  }, []);

  const availableForHome = useMemo(
    () => filterBySearch(teamAParticipants, searchHome),
    [teamAParticipants, searchHome, filterBySearch]
  );
  const availableForAway = useMemo(
    () => filterBySearch(teamBParticipants, searchAway),
    [teamBParticipants, searchAway, filterBySearch]
  );

  const playedCountMap = useMemo(() => {
    const countMap = new Map<number, number>();
    allGames.forEach((game) => {
      (game.lineups || []).forEach((lineup) => {
        const pid = lineup.player_participant_id;
        countMap.set(pid, (countMap.get(pid) || 0) + 1);
      });
    });
    return countMap;
  }, [allGames]);

  const getPlayedLabel = useCallback(
    (participantId: number) => {
      const count = playedCountMap.get(participantId) || 0;
      return `Played: ${count}x`;
    },
    [playedCountMap]
  );

  const savedTeams = useMemo(() => {
    const fromGames = buildSavedTeamsFromGames(allGames);
    const customTeams: SavedTeamItem[] = [];

    // Helper map to lookup existing player number by team name and participant id
    const knownNumberMap = new Map<string, number>();
    fromGames.forEach((t) => {
      const tKey = t.teamName.trim().toLowerCase();
      t.lineup.forEach((slot) => {
        if (slot.playerNumber !== null && slot.playerNumber !== undefined) {
          knownNumberMap.set(`${tKey}|${slot.participantId}`, slot.playerNumber);
        }
      });
    });

    // Find custom form question for Team (template === 'team' or title containing 'tim')
    const teamField = eventCustomForm?.find(
      (f) => f.template === 'team' || (f.title && f.title.toLowerCase().includes('tim'))
    );

    if (teamField && teamField.options && teamField.options.length > 0) {
      const isFutsal = eventSportCategorySlug.toLowerCase().includes('futsal');
      teamField.options.forEach((teamOption) => {
        const optionTrimmed = teamOption.trim();
        if (!optionTrimmed) return;

        // Find confirmed participants belonging to this team
        const matchingParticipants = confirmedParticipants.filter((p) => {
          const t = getParticipantEffectiveTeam(p);
          return t && t.toLowerCase() === optionTrimmed.toLowerCase();
        });

        const optKey = optionTrimmed.toLowerCase();
        customTeams.push({
          teamName: optionTrimmed,
          lineup: matchingParticipants.map((p, idx) => {
            const pid = toParticipantId(p.id);
            const knownNum =
              knownNumberMap.get(`${optKey}|${pid}`) ??
              getParticipantEffectivePlayerNumber(p);
            return {
              participantId: pid,
              name: p.name,
              role: getDefaultRoleFromPosition(getParticipantEffectivePosition(p), p.role, isFutsal),
              isStarting: idx < formatNumber,
              playerNumber: knownNum,
            };
          }),
        });
      });
    }

    const customMap = new Map<string, SavedTeamItem>();
    customTeams.forEach((ct) => {
      if (ct.lineup.length > 0) {
        customMap.set(ct.teamName.toLowerCase(), ct);
      }
    });

    const merged: SavedTeamItem[] = [];
    customMap.forEach((ct) => merged.push(ct));

    fromGames.forEach((fg) => {
      if (!customMap.has(fg.teamName.toLowerCase())) {
        const enrichedLineup = fg.lineup.map((slot) => {
          if (slot.playerNumber !== null && slot.playerNumber !== undefined) return slot;
          const part = confirmedParticipants.find((p) => toParticipantId(p.id) === slot.participantId);
          return {
            ...slot,
            playerNumber: part ? getParticipantEffectivePlayerNumber(part) : null,
          };
        });
        merged.push({ ...fg, lineup: enrichedLineup });
      }
    });

    return merged;
  }, [
    allGames,
    eventCustomForm,
    confirmedParticipants,
    eventSportCategorySlug,
    formatNumber,
  ]);

  const [currentGameData, setCurrentGameData] = useState<GameDetailResponse | null>(null);

  const savedTeamsForHome = useMemo(
    () => savedTeams.filter((t) => getSavedTeamKey(t) !== selectedSavedTeamKeyForAway),
    [savedTeams, selectedSavedTeamKeyForAway]
  );

  const savedTeamsForAway = useMemo(
    () => savedTeams.filter((t) => getSavedTeamKey(t) !== selectedSavedTeamKeyForHome),
    [savedTeams, selectedSavedTeamKeyForHome]
  );

  useEffect(() => {
    if (!isOpen || !eventSlug) return;
    setLoadingParticipants(true);
    setError(null);
    communityService
      .getEventParticipants(eventSlug, { limit: 500 })
      .then((res) => setParticipants(res.participants ?? []))
      .catch(() => setParticipants([]))
      .finally(() => setLoadingParticipants(false));
    communityService
      .getGamesByEventSlug(eventSlug)
      .then(async (gamesList: GameListItem[]) => {
        const details = await Promise.all(
          gamesList.map((g) => communityService.getGameBySlug(g.slug).catch(() => null))
        );
        setAllGames(details.filter((d): d is GameDetailResponse => d !== null));
      })
      .catch(() => setAllGames([]));
  }, [isOpen, eventSlug]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentGameData(null);
      return;
    }
    if (gameSlug) {
      setLoadingGame(true);
      setError(null);
      communityService
        .getGameBySlug(gameSlug)
        .then((game: GameDetailResponse) => {
          setCurrentGameData(game);
          setHomeTeamName(game.home_team_name || (isSparring ? teamAName : 'Tim A'));
          setAwayTeamName(game.away_team_name || (isSparring ? teamBName : 'Tim B'));
          const parsed = parseFormatFromPlayingStyle(game.playing_style);
          setFormatNumber(
            parsed ??
              (eventSportCategorySlug === 'futsal'
                ? DEFAULT_FORMAT_FUTSAL
                : DEFAULT_FORMAT_MINI_SOCCER)
          );
          setHomeSideMode('lineup');
          setAwaySideMode('lineup');
        })
        .catch(() => setError('Gagal memuat data game'))
        .finally(() => setLoadingGame(false));
    } else {
      setCurrentGameData(null);
      setHomeTeamName(isSparring ? teamAName : 'Tim A');
      setAwayTeamName(isSparring ? teamBName : 'Tim B');
      setFormatNumber(
        eventSportCategorySlug === 'futsal' ? DEFAULT_FORMAT_FUTSAL : DEFAULT_FORMAT_MINI_SOCCER
      );
      setHomeLineup([]);
      setAwayLineup([]);
      setSearchHome('');
      setSearchAway('');
      setHomeSideMode(isSparring ? 'lineup' : 'choice');
      setAwaySideMode(isSparring ? 'lineup' : 'choice');
      setGenerateErrorHome(null);
      setGenerateErrorAway(null);
      setSelectedSavedTeamKeyForHome(null);
      setSelectedSavedTeamKeyForAway(null);
    }
  }, [isOpen, gameSlug, eventSportCategorySlug, isSparring, teamAName, teamBName]);

  useEffect(() => {
    if (!isOpen || !gameSlug || !currentGameData) return;
    const isFutsal = eventSportCategorySlug.toLowerCase().includes('futsal');
    const homeTeamKey = (currentGameData.home_team_name || '').trim().toLowerCase();
    const awayTeamKey = (currentGameData.away_team_name || '').trim().toLowerCase();

    // Map known numbers from other games
    const knownNumberMap = new Map<string, number>();
    allGames.forEach((g) => {
      if (g.slug === gameSlug) return;
      (g.lineups || []).forEach((l) => {
        if (l.player_number !== null && l.player_number !== undefined) {
          const tName = (l.team_side === 'home' ? g.home_team_name : g.away_team_name) || '';
          knownNumberMap.set(
            `${tName.trim().toLowerCase()}|${toParticipantId(l.player_participant_id)}`,
            l.player_number
          );
        }
      });
    });

    const home = (currentGameData.lineups || [])
      .filter((l) => l.team_side === 'home')
      .map((l) => {
        const pid = toParticipantId(l.player_participant_id);
        const part = participants.find((p) => toParticipantId(p.id) === pid);
        const mappedRole = getDefaultRoleFromPosition(
          part?.position,
          l.role || part?.role,
          isFutsal
        );
        const formPlayerNumber = part ? getParticipantEffectivePlayerNumber(part) : null;
        const resolvedNumber =
          l.player_number !== null && l.player_number !== undefined
            ? l.player_number
            : (knownNumberMap.get(`${homeTeamKey}|${pid}`) ?? formPlayerNumber);
        return {
          participantId: pid,
          name: l.name,
          role: mappedRole,
          isStarting: l.is_starting,
          playerNumber: resolvedNumber,
        };
      });
    const away = (currentGameData.lineups || [])
      .filter((l) => l.team_side === 'away')
      .map((l) => {
        const pid = toParticipantId(l.player_participant_id);
        const part = participants.find((p) => toParticipantId(p.id) === pid);
        const mappedRole = getDefaultRoleFromPosition(
          part?.position,
          l.role || part?.role,
          isFutsal
        );
        const formPlayerNumber = part ? getParticipantEffectivePlayerNumber(part) : null;
        const resolvedNumber =
          l.player_number !== null && l.player_number !== undefined
            ? l.player_number
            : (knownNumberMap.get(`${awayTeamKey}|${pid}`) ?? formPlayerNumber);
        return {
          participantId: pid,
          name: l.name,
          role: mappedRole,
          isStarting: l.is_starting,
          playerNumber: resolvedNumber,
        };
      });
    setHomeLineup(home);
    setAwayLineup(away);
  }, [isOpen, gameSlug, currentGameData, participants, eventSportCategorySlug, allGames]);

  const resetForm = useCallback(() => {
    setHomeTeamName(isSparring ? teamAName : 'Tim A');
    setAwayTeamName(isSparring ? teamBName : 'Tim B');
    setHomeLineup([]);
    setAwayLineup([]);
    setSearchHome('');
    setSearchAway('');
    setError(null);
    setHomeSideMode(isSparring ? 'lineup' : 'choice');
    setAwaySideMode(isSparring ? 'lineup' : 'choice');
    setGenerateErrorHome(null);
    setGenerateErrorAway(null);
    setSelectedSavedTeamKeyForHome(null);
    setSelectedSavedTeamKeyForAway(null);
  }, [isSparring, teamAName, teamBName]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const addToTeam = useCallback(
    (team: 'home' | 'away', p: MabarParticipant, asStarting: boolean) => {
      const id = toParticipantId(p.id);
      if (!id) return;
      const isFutsal = eventSportCategorySlug.toLowerCase().includes('futsal');
      const effPos = getParticipantEffectivePosition(p);
      const initialRole = getDefaultRoleFromPosition(effPos, p.role, isFutsal);

      // Look up known player number for this participant in this team
      const currentTeamName = (team === 'home' ? homeTeamName : awayTeamName).trim().toLowerCase();
      let knownNum: number | null = null;
      allGames.forEach((g) => {
        (g.lineups || []).forEach((l) => {
          if (
            toParticipantId(l.player_participant_id) === id &&
            l.player_number !== null &&
            l.player_number !== undefined
          ) {
            const sideTeam = (l.team_side === 'home' ? g.home_team_name : g.away_team_name) || '';
            if (sideTeam.trim().toLowerCase() === currentTeamName) {
              knownNum = l.player_number;
            }
          }
        });
      });

      if (knownNum === null) {
        knownNum = getParticipantEffectivePlayerNumber(p);
      }

      const slot: LineupSlot = {
        participantId: id,
        name: p.name,
        role: initialRole,
        isStarting: asStarting,
        playerNumber: knownNum,
      };
      if (team === 'home') {
        const starting = homeLineup.filter((s) => s.isStarting).length;
        if (asStarting && starting >= maxStarting) return;
        setHomeLineup((prev) => [...prev, slot]);
      } else {
        const starting = awayLineup.filter((s) => s.isStarting).length;
        if (asStarting && starting >= maxStarting) return;
        setAwayLineup((prev) => [...prev, slot]);
      }
    },
    [homeLineup, awayLineup, maxStarting, eventSportCategorySlug, homeTeamName, awayTeamName, allGames]
  );

  const removeFromTeam = useCallback((team: 'home' | 'away', participantId: number) => {
    if (team === 'home')
      setHomeLineup((prev) => prev.filter((s) => s.participantId !== participantId));
    else setAwayLineup((prev) => prev.filter((s) => s.participantId !== participantId));
  }, []);

  const toggleStarting = useCallback((team: 'home' | 'away', participantId: number) => {
    if (team === 'home') {
      setHomeLineup((prev) =>
        prev.map((s) =>
          s.participantId === participantId ? { ...s, isStarting: !s.isStarting } : s
        )
      );
    } else {
      setAwayLineup((prev) =>
        prev.map((s) =>
          s.participantId === participantId ? { ...s, isStarting: !s.isStarting } : s
        )
      );
    }
  }, []);

  const handleSelectSavedTeamHome = useCallback((tName: string, lineup: LineupSlot[]) => {
    setHomeTeamName(tName);
    setHomeLineup(lineup);
    setHomeSideMode('lineup');
    const key = `${tName}|${lineup
      .map((s) => s.participantId)
      .sort((a, b) => a - b)
      .join(',')}`;
    setSelectedSavedTeamKeyForHome(key);
  }, []);

  const handleSelectSavedTeamAway = useCallback((tName: string, lineup: LineupSlot[]) => {
    setAwayTeamName(tName);
    setAwayLineup(lineup);
    setAwaySideMode('lineup');
    const key = `${tName}|${lineup
      .map((s) => s.participantId)
      .sort((a, b) => a - b)
      .join(',')}`;
    setSelectedSavedTeamKeyForAway(key);
  }, []);

  const onChoiceTimBaruHome = useCallback(() => {
    setHomeTeamName((prev) => (prev && prev.trim() !== '' ? prev : isSparring ? teamAName : 'Tim A'));
    setHomeLineup([]);
    setHomeSideMode('lineup');
    setSelectedSavedTeamKeyForHome(null);
  }, [isSparring, teamAName]);

  const onChoiceTimBaruAway = useCallback(() => {
    setAwayTeamName((prev) => (prev && prev.trim() !== '' ? prev : isSparring ? teamBName : 'Tim B'));
    setAwayLineup([]);
    setAwaySideMode('lineup');
    setSelectedSavedTeamKeyForAway(null);
  }, [isSparring, teamBName]);

  const handleGenerateHome = useCallback(() => {
    setGenerateErrorHome(null);
    const available = availableParticipants.filter((p) => {
      const id = toParticipantId(p.id);
      return id > 0 && !homeLineup.some((s) => s.participantId === id);
    });

    const targetList = isSparring
      ? available.filter(
          (p) => !p.team || p.team.trim().toLowerCase() === teamAName.trim().toLowerCase()
        )
      : available;

    if (targetList.length < minPlayers) {
      setGenerateErrorHome(
        `Peserta ${isSparring ? teamAName : 'Tim A'} kurang (min. ${minPlayers})`
      );
      return;
    }
    const isFutsal = eventSportCategorySlug.toLowerCase().includes('futsal');
    const mappedTargetList = targetList.map((p) => ({
      ...p,
      position: getParticipantEffectivePosition(p) ?? p.position,
    }));
    const res = generateOneTeam(mappedTargetList, playedCountMap, formatNumber, isFutsal);
    if (res.error) {
      setGenerateErrorHome(res.error);
      return;
    }
    setHomeLineup(res.lineup);
    setHomeTeamName((prev) => (prev && prev.trim() !== '' ? prev : isSparring ? teamAName : 'Tim A'));
    setHomeSideMode('lineup');
  }, [
    availableParticipants,
    homeLineup,
    isSparring,
    teamAName,
    minPlayers,
    formatNumber,
    playedCountMap,
    eventSportCategorySlug,
  ]);

  const handleGenerateAway = useCallback(() => {
    setGenerateErrorAway(null);
    const available = availableParticipants.filter((p) => {
      const id = toParticipantId(p.id);
      return id > 0 && !awayLineup.some((s) => s.participantId === id);
    });

    const targetList = isSparring
      ? available.filter(
          (p) => p.team && p.team.trim().toLowerCase() === teamBName.trim().toLowerCase()
        )
      : available;

    if (targetList.length < minPlayers) {
      setGenerateErrorAway(
        `Peserta ${isSparring ? teamBName : 'Tim B'} kurang (min. ${minPlayers})`
      );
      return;
    }
    const isFutsal = eventSportCategorySlug.toLowerCase().includes('futsal');
    const mappedTargetList = targetList.map((p) => ({
      ...p,
      position: getParticipantEffectivePosition(p) ?? p.position,
    }));
    const res = generateOneTeam(mappedTargetList, playedCountMap, formatNumber, isFutsal);
    if (res.error) {
      setGenerateErrorAway(res.error);
      return;
    }
    setAwayLineup(res.lineup);
    setAwayTeamName((prev) => (prev && prev.trim() !== '' ? prev : isSparring ? teamBName : 'Tim B'));
    setAwaySideMode('lineup');
  }, [
    availableParticipants,
    awayLineup,
    isSparring,
    teamBName,
    minPlayers,
    formatNumber,
    playedCountMap,
    eventSportCategorySlug,
  ]);

  const handleRoleChangeHome = useCallback((participantId: number, newRole: string) => {
    setHomeLineup((prev) =>
      prev.map((s) => (s.participantId === participantId ? { ...s, role: newRole } : s))
    );
  }, []);

  const handleRoleChangeAway = useCallback((participantId: number, newRole: string) => {
    setAwayLineup((prev) =>
      prev.map((s) => (s.participantId === participantId ? { ...s, role: newRole } : s))
    );
  }, []);

  const handlePlayerNumberChangeHome = useCallback(
    (participantId: number, newNumber: number | null) => {
      setHomeLineup((prev) =>
        prev.map((s) =>
          s.participantId === participantId ? { ...s, playerNumber: newNumber } : s
        )
      );
    },
    []
  );

  const handlePlayerNumberChangeAway = useCallback(
    (participantId: number, newNumber: number | null) => {
      setAwayLineup((prev) =>
        prev.map((s) =>
          s.participantId === participantId ? { ...s, playerNumber: newNumber } : s
        )
      );
    },
    []
  );

  const buildPayload = useCallback((): CreateGamePayload => {
    const toEntry = (s: LineupSlot): CreateGameLineupEntry => ({
      participant_id: s.participantId,
      is_starting: s.isStarting,
      player_number: s.playerNumber ?? undefined,
      role: s.role || undefined,
    });
    return {
      home_team_name: homeTeamName.trim() || (isSparring ? teamAName : 'Tim A'),
      away_team_name: awayTeamName.trim() || (isSparring ? teamBName : 'Tim B'),
      home_lineup: homeLineup.map(toEntry),
      away_lineup: awayLineup.map(toEntry),
      playing_style: JSON.stringify({ format: `${formatNumber}:${formatNumber}` }),
    };
  }, [
    homeTeamName,
    awayTeamName,
    homeLineup,
    awayLineup,
    formatNumber,
    isSparring,
    teamAName,
    teamBName,
  ]);

  const homeStartingCount = homeLineup.filter((s) => s.isStarting).length;
  const awayStartingCount = awayLineup.filter((s) => s.isStarting).length;

  const canSave =
    homeTeamName.trim().length > 0 &&
    awayTeamName.trim().length > 0 &&
    homeLineup.length >= minPlayers &&
    awayLineup.length >= minPlayers &&
    homeStartingCount <= maxStarting &&
    awayStartingCount <= maxStarting;

  const syncPlayerNumbersToOtherGames = useCallback(
    async (currentSlug?: string) => {
      if (!eventSlug) return;
      const homeTeamNorm = homeTeamName.trim().toLowerCase();
      const awayTeamNorm = awayTeamName.trim().toLowerCase();

      // Collect non-null player numbers from current lineups
      const homeNumMap = new Map<number, number>();
      homeLineup.forEach((slot) => {
        if (slot.playerNumber !== null && slot.playerNumber !== undefined) {
          homeNumMap.set(slot.participantId, slot.playerNumber);
        }
      });

      const awayNumMap = new Map<number, number>();
      awayLineup.forEach((slot) => {
        if (slot.playerNumber !== null && slot.playerNumber !== undefined) {
          awayNumMap.set(slot.participantId, slot.playerNumber);
        }
      });

      if (homeNumMap.size === 0 && awayNumMap.size === 0) return;

      // Find other games in allGames
      for (const otherGame of allGames) {
        if (otherGame.slug === currentSlug) continue;

        const ogHomeNorm = (otherGame.home_team_name || '').trim().toLowerCase();
        const ogAwayNorm = (otherGame.away_team_name || '').trim().toLowerCase();

        let hasChanges = false;
        const newHomeLineups: CreateGameLineupEntry[] = (otherGame.lineups || [])
          .filter((l) => l.team_side === 'home')
          .map((l) => {
            const pid = toParticipantId(l.player_participant_id);
            let targetNum = l.player_number ?? null;

            if (ogHomeNorm === homeTeamNorm && homeNumMap.has(pid)) {
              const num = homeNumMap.get(pid)!;
              if (targetNum !== num) {
                targetNum = num;
                hasChanges = true;
              }
            } else if (ogHomeNorm === awayTeamNorm && awayNumMap.has(pid)) {
              const num = awayNumMap.get(pid)!;
              if (targetNum !== num) {
                targetNum = num;
                hasChanges = true;
              }
            }

            return {
              participant_id: pid,
              is_starting: l.is_starting,
              player_number: targetNum ?? undefined,
              role: l.role || undefined,
            };
          });

        const newAwayLineups: CreateGameLineupEntry[] = (otherGame.lineups || [])
          .filter((l) => l.team_side === 'away')
          .map((l) => {
            const pid = toParticipantId(l.player_participant_id);
            let targetNum = l.player_number ?? null;

            if (ogAwayNorm === homeTeamNorm && homeNumMap.has(pid)) {
              const num = homeNumMap.get(pid)!;
              if (targetNum !== num) {
                targetNum = num;
                hasChanges = true;
              }
            } else if (ogAwayNorm === awayTeamNorm && awayNumMap.has(pid)) {
              const num = awayNumMap.get(pid)!;
              if (targetNum !== num) {
                targetNum = num;
                hasChanges = true;
              }
            }

            return {
              participant_id: pid,
              is_starting: l.is_starting,
              player_number: targetNum ?? undefined,
              role: l.role || undefined,
            };
          });

        if (hasChanges) {
          try {
            await communityService.updateGame(otherGame.slug, {
              home_team_name: otherGame.home_team_name,
              away_team_name: otherGame.away_team_name,
              playing_style: otherGame.playing_style ?? undefined,
              home_lineup: newHomeLineups,
              away_lineup: newAwayLineups,
            });
          } catch (syncErr) {
            console.error('Failed to sync player numbers to game:', otherGame.slug, syncErr);
          }
        }
      }
    },
    [eventSlug, homeTeamName, awayTeamName, homeLineup, awayLineup, allGames]
  );

  const handleSave = useCallback(async () => {
    if (!canSave || saving) return;
    setError(null);
    setSaving(true);
    try {
      if (isEditMode && gameSlug) {
        await communityService.updateGame(gameSlug, buildPayload());
        await syncPlayerNumbersToOtherGames(gameSlug);
        onSuccess(true);
      } else {
        const created = await communityService.createGame(eventSlug, buildPayload());
        if (created?.slug) {
          await syncPlayerNumbersToOtherGames(created.slug);
        }
        onSuccess(false);
      }
      handleClose();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(
        msg ||
          (err instanceof Error
            ? err.message
            : isEditMode
              ? 'Gagal memperbarui game'
              : 'Gagal menyimpan game')
      );
    } finally {
      setSaving(false);
    }
  }, [
    canSave,
    saving,
    isEditMode,
    gameSlug,
    buildPayload,
    syncPlayerNumbersToOtherGames,
    onSuccess,
    eventSlug,
    handleClose,
  ]);

  return {
    isEditMode,
    loading: loadingParticipants || loadingGame,
    saving,
    error,
    sportLabel,
    formatNumber,
    setFormatNumber,
    maxStarting,
    minPlayers,
    homeTeamName,
    setHomeTeamName,
    awayTeamName,
    setAwayTeamName,
    homeLineup,
    awayLineup,
    homeSideMode,
    setHomeSideMode,
    awaySideMode,
    setAwaySideMode,
    searchHome,
    setSearchHome,
    searchAway,
    setSearchAway,
    availableForHome,
    availableForAway,
    getPlayedLabel,
    savedTeams,
    savedTeamsForHome,
    savedTeamsForAway,
    generateErrorHome,
    generateErrorAway,
    handleSelectSavedTeamHome,
    handleSelectSavedTeamAway,
    onChoiceTimBaruHome,
    onChoiceTimBaruAway,
    handleGenerateHome,
    handleGenerateAway,
    handleRoleChangeHome,
    handleRoleChangeAway,
    handlePlayerNumberChangeHome,
    handlePlayerNumberChangeAway,
    addToTeam,
    removeFromTeam,
    toggleStarting,
    resetForm,
    handleClose,
    handleSave,
    canSave,
    isSparring,
    teamAName,
    teamBName,
  };
}
